/**
 * @file GridHeading.tsx
 * @brief The drawer's section heading: a soft pill with the label, a small
 *        dot, then a 1px line to the row's end, an optional action at the
 *        line's far end. Web parity for the phone's `VZGridHeading`.
 *
 * @details
 * Phone source: FRONTEND/src/shared/components/VZUIKit/VZGridSection.tsx
 * (`VZGridHeading`, `createStyles`) and VZFieldset.tsx (`VZSectionPill`,
 * `vzSectionPillColor`).
 * - pill, dot and line share one colour: light = white under the warm wash
 *   rgba(50,38,18,0.14) = rgb(226,225,222); dark = surfaceRaised (--app-surface-raised);
 * - label bold 15px, theme ink at 50% opacity;
 * - the row is `items-center`, so the line sits on the pill's vertical
 *   middle and the action is centred on that same line (DES standing rule,
 *   08-10-2026, "line middle main hoo, according to the pill").
 * `dir` sets the row's direction; without it the row inherits its
 * container's. Spacing is logical (ms-), so an RTL row mirrors cleanly. The
 * meeting review holds its headings LTR even in RTL content (DES 08-10-2026,
 * "heading aur notes agendas ko rtl ki zaroorat nahin hai").
 *
 * @author DES
 * @date 08-10-2026
 */

import type { ReactNode } from "react";
import { cn } from "@/shared/theme/cn";

/** One colour for the pill, its dot and its line. */
const PILL_BG = "bg-[rgb(226,225,222)] dark:bg-app-surface-raised";

export interface GridHeadingProps {
  label: string;
  /** A control at the line's far end (Share/Export pill, ⋮ menu, tips icon). */
  action?: ReactNode;
  /** The row's direction; inherited when omitted. */
  dir?: "ltr" | "rtl";
  className?: string;
  /** Heading level for the label (defaults to h3). */
  as?: "h2" | "h3";
}

export function GridHeading({ label, action, dir, className, as = "h3" }: GridHeadingProps) {
  const Tag = as;
  return (
    <div dir={dir} className={cn("flex items-center", className)}>
      <span className={cn("inline-flex shrink-0 items-center rounded-full px-3 py-[5px]", PILL_BG)}>
        <Tag className="text-[15px] font-bold leading-5 tracking-[-0.2px] text-app-text opacity-50">
          {label}
        </Tag>
      </span>
      <span aria-hidden className={cn("ms-1.5 h-[5px] w-[5px] shrink-0 rounded-full", PILL_BG)} />
      <span aria-hidden className={cn("h-px min-w-4 flex-1", PILL_BG)} />
      {action ? <div className="ms-2 flex shrink-0 items-center gap-2">{action}</div> : null}
    </div>
  );
}

export default GridHeading;
